/**
 * Runtime — the canonical V2 generation path.
 *
 * One route from an admin's wizard brief to editable iVisora templates:
 *
 *   brief → research → memory → thinking → library → art direction (plan)
 *         → [ per concept: OpenAI designs → schema → editor → art direction ]
 *         → renderer → editor session
 *
 * Concepts are designed ONE AT A TIME, not requested in a batch. Before each
 * one, Design Memory is asked what the concepts already produced in this run
 * look like, and the constraints it derives go into the prompt. After each one,
 * the accepted design is remembered. That loop is the difference between three
 * genuinely different concepts and one concept in three colourways.
 *
 * Deterministic apart from the model itself: same brief, same plan, same
 * patterns, same constraints.
 */

import { DesignMemory } from '../memory';
import { describeDocument } from './describeDocument';
import { generateConcept, DEFAULT_MAX_REPAIRS } from './generateConcept';

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

/** Scope for this run's memory — concepts compare against their own batch. */
export const RUN_SCOPE = { type: 'batch', key: 'generation-run' };

/**
 * Generate `count` independently designed concepts.
 *
 * @param {Object} inputs
 * @param {Object} inputs.plan       APPROVED Creative Plan.
 * @param {Object} inputs.research   Research Brief.
 * @param {Object} inputs.genome     Style Genome.
 * @param {Object[]} inputs.patterns Selected Library Patterns.
 * @param {Object} [inputs.constraints]  constraints already derived for the brief.
 * @param {Object} [options]
 * @param {number} [options.count]
 * @param {DesignMemory} [options.memory]  injected so a durable store can replace it.
 * @param {number} [options.maxRepairs]
 * @param {Object} [options.provider]
 * @param {AbortSignal} [options.signal]
 * @param {Function} [options.onProgress] ({ concept, total, attempt, stage }).
 * @returns {Promise<Object>} { concepts, rejected, stats }
 */
export const runGeneration = async (inputs = {}, options = {}) => {
  const total = Math.max(1, Number(options.count) || 1);
  const memory = options.memory instanceof DesignMemory ? options.memory : new DesignMemory();
  const maxRepairs = Number.isInteger(options.maxRepairs) ? options.maxRepairs : DEFAULT_MAX_REPAIRS;

  const concepts = [];
  const rejected = [];
  const priorDescriptors = [];

  for (let index = 0; index < total; index += 1) {
    if (options.signal && options.signal.aborted) break;

    // What has this run already produced? The answer shapes the next request.
    const { constraints } = memory.check(
      { ...asObject(inputs.baseDescriptor), genome: asObject(inputs.genome).id },
      { scope: RUN_SCOPE },
    );

    const merged = {
      ...asObject(inputs),
      // Constraints derived for the brief, plus what this run has produced.
      constraints: index === 0 ? asObject(inputs.constraints) : constraints,
      priorConcepts: priorDescriptors,
    };

    const result = await generateConcept(merged, {
      maxRepairs,
      // Memory belongs to the run, so the uniqueness gate is supplied here
      // rather than reached for inside a single concept's repair loop.
      checkDuplicate: (document) => {
        const descriptor = describeDocument(document, inputs);
        const fingerprint = memory.fingerprint(descriptor);

        // Two checks, because they catch different things.
        //
        // An EXACT repeat is invisible to the similarity report: a fingerprint
        // id is a content hash, and the report skips any stored fingerprint
        // whose id matches the candidate's as "never compare against self".
        // That rule is right for re-checking a design already in memory and
        // wrong here, so the identical case is detected by id directly.
        const repeated = memory.store
          .query({ scope: RUN_SCOPE })
          .some((f) => f.id === fingerprint.id);

        // A NEAR duplicate — different in wording, same in everything that
        // matters — is what the similarity report is actually for.
        const { constraints: c } = memory.check(descriptor, { scope: RUN_SCOPE });

        return {
          duplicate: repeated || Boolean(c.diversityConstraints.duplicate),
          axes: repeated
            ? ['composition', 'hierarchy', 'typography', 'colour']
            : c.diversityConstraints.tooSimilarDimensions,
        };
      },
      provider: options.provider,
      signal: options.signal,
      model: options.model,
      thresholds: options.thresholds,
      sessionOptions: options.sessionOptions,
      onAttempt: ({ attempt, corrections }) => {
        if (typeof options.onProgress === 'function') {
          options.onProgress({ concept: index + 1, total, attempt, repairing: corrections.length > 0 });
        }
      },
    });

    if (!result.ok) {
      rejected.push({ index, corrections: result.corrections, history: result.history });
      continue;
    }

    // Remember it, so the next concept is designed against it rather than beside it.
    const descriptor = describeDocument(result.document, inputs);
    memory.remember(descriptor, { scope: RUN_SCOPE });
    priorDescriptors.push(descriptor);

    concepts.push({ index, ...result, descriptor });
  }

  return {
    concepts,
    rejected,
    stats: {
      requested: total,
      produced: concepts.length,
      rejectedCount: rejected.length,
      repairsUsed: concepts.reduce((n, c) => n + (c.attempts - 1), 0),
      approvedOnMerit: concepts.filter((c) => c.approvedOnMerit).length,
      // Concepts that repeated an earlier one and were kept only because the
      // repair budget ran out. Nonzero means the batch is less varied than asked.
      duplicates: concepts.filter((c) => c.duplicate).length,
      maxRepairs,
    },
  };
};

/**
 * How different the produced concepts actually are, as the caller's evidence
 * that the multi-concept requirement was met rather than merely attempted.
 *
 * @param {Object[]} concepts  from runGeneration.
 * @param {DesignMemory} [memory]
 * @returns {Object} { pairs, maxSimilarity, distinct }
 */
export const conceptDivergence = (concepts, memory = new DesignMemory()) => {
  const list = asArray(concepts);
  const pairs = [];
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const a = memory.fingerprint(list[i].descriptor);
      const { report } = memory.check(list[j].descriptor, { scope: { type: 'batch', key: `pair-${i}-${j}` } });
      // An empty store yields 0; compare directly instead.
      const similarity = report.perDimensionMax
        ? report.maxSimilarity
        : 0;
      pairs.push({ a: i, b: j, similarity, fingerprintA: a.id });
    }
  }
  const maxSimilarity = pairs.reduce((m, p) => Math.max(m, p.similarity), 0);
  return { pairs, maxSimilarity, distinct: maxSimilarity < 1 };
};
